/**
 * MarketplaceReportModal - Modal for reporting problematic listings
 */

import React, { useState } from 'react';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import { REPORT_REASONS } from './MarketplaceCategories';

const MarketplaceReportModal = ({ listingId, listingTitle, onClose }) => {
  const { reportListing, reportReasons } = useMarketplace();
  
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const reasons = reportReasons || REPORT_REASONS;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedReason) {
      setError('Please select a reason for your report');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await reportListing(listingId, selectedReason, details);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error submitting report:', err);
      setError(err.message || 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Modal isOpen onClose={onClose} title="Report Submitted">
        <div className="text-center py-4">
          <div className="text-4xl mb-4">✅</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Thank you for your report
          </h3>
          <p className="text-gray-600">
            We'll review this listing and take appropriate action.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen onClose={onClose} title="Report Listing">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-600">
            Reporting: <span className="font-medium text-gray-900">{listingTitle}</span>
          </p>
        </div>

        {/* Reason selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Why are you reporting this listing? <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            {reasons.map(reason => (
              <label
                key={reason.id}
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedReason === reason.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="reportReason"
                  value={reason.id}
                  checked={selectedReason === reason.id}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-900">{reason.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Additional details */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional details (optional)
          </label>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Provide any additional context that might help us review this report..."
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            {details.length}/500 characters
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={submitting || !selectedReason}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default MarketplaceReportModal;
